import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Key, Clock, MapPin, WifiOff, Wifi, RefreshCw, DoorOpen, Loader2, CalendarPlus, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { fetchMyBookings, getCachedBookings } from '../lib/bookingsStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brussels-time helper (avoid timezone surprises)
function nowBrussels() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Brussels' }));
}

function parseLocal(dateStr, timeStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = (timeStr || '00:00').split(':').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
}

function formatRelative(target, now, t) {
  if (!target) return '';
  const diffMin = Math.round((target - now) / 60000);
  if (diffMin <= -60 * 24) return target.toLocaleDateString();
  if (diffMin < -60) return t('myAccess.endedHoursAgo', { count: Math.round(-diffMin / 60) });
  if (diffMin < 0) return t('myAccess.endedMinAgo', { count: -diffMin });
  if (diffMin < 60) return t('myAccess.inMinutes', { count: diffMin });
  if (diffMin < 60 * 24) return t('myAccess.inHours', { count: Math.round(diffMin / 60) });
  return t('myAccess.inDays', { count: Math.round(diffMin / 60 / 24) });
}

function formatDateLong(dateStr, locale) {
  if (!dateStr) return '';
  try {
    const d = parseLocal(dateStr, '00:00');
    return d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  } catch {
    return dateStr;
  }
}

function statusOf(b, now) {
  // Returns one of: 'past', 'active', 'upcoming-soon' (<2h), 'upcoming'
  const start = parseLocal(b.date, b.start_time || '00:00');
  const end = parseLocal(b.date, b.end_time || '23:59');
  if (!start || !end) return 'upcoming';
  const grace = 15 * 60 * 1000;
  if (now > new Date(end.getTime() + grace)) return 'past';
  if (now >= new Date(start.getTime() - grace) && now <= new Date(end.getTime() + grace)) return 'active';
  if (start - now < 2 * 60 * 60 * 1000) return 'upcoming-soon';
  return 'upcoming';
}

export default function MyAccessPage() {
  const { t, i18n } = useTranslation();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [cachedAt, setCachedAt] = useState(null);
  const [fromCache, setFromCache] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tick, setTick] = useState(0);
  const [openingId, setOpeningId] = useState(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await fetchMyBookings(token);
      setBookings(res.bookings || []);
      setCachedAt(res.cachedAt);
      setFromCache(Boolean(res.fromCache));
    } catch (err) {
      const cached = await getCachedBookings();
      setBookings(cached.bookings || []);
      setCachedAt(cached.cachedAt);
      setFromCache(true);
      if (!cached.bookings?.length) {
        toast.error(t('myAccess.errorNoCache', 'Connexion impossible et aucune donnée hors-ligne.'));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/auth?redirect=/mes-acces');
      return;
    }
    refresh();
  }, [user, authLoading, navigate, refresh]);

  // Online/offline listeners
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Tick every 30s for relative timers
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const now = nowBrussels();
  const sorted = useMemo(() => {
    const filtered = (bookings || []).filter((b) => b.status === 'confirmed' && b.access_code);
    return filtered
      .map((b) => ({ ...b, _start: parseLocal(b.date, b.start_time || '00:00') }))
      .sort((a, b) => (a._start?.getTime() || 0) - (b._start?.getTime() || 0));
  }, [bookings]);

  const upcoming = sorted.filter((b) => statusOf(b, now) !== 'past');
  const past = sorted.filter((b) => statusOf(b, now) === 'past').slice(-5).reverse();

  async function openDoor(b) {
    if (!online) {
      toast.error(t('myAccess.offlineCantOpen', 'Hors ligne — utilisez le clavier de la porte avec votre code.'));
      return;
    }
    setOpeningId(b.booking_id);
    try {
      const res = await axios.post(
        `${API}/access/user/open`,
        { booking_id: b.booking_id, duration_s: 5 },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 15000 }
      );
      if (res.data?.status === 'acked') {
        toast.success(t('myAccess.doorOpened', 'Porte ouverte ✓'));
      } else if (res.data?.status === 'timeout') {
        toast.warning(t('myAccess.doorPending', "La commande est en attente. La porte s'ouvrira dès que le boîtier répond."));
      } else {
        toast.error(t('myAccess.doorFailed', 'Ouverture impossible.'));
      }
    } catch (err) {
      const msg = err.response?.data?.detail || t('myAccess.doorFailed', 'Ouverture impossible.');
      toast.error(msg);
    } finally {
      setOpeningId(null);
    }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A3C34]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24" data-testid="my-access-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-[#1A3C34] text-white px-4 py-3 shadow-md">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/m')}
              data-testid="my-access-back-btn"
              aria-label="Back to hub"
              className="text-white/70 hover:text-white p-1.5 -ml-1.5"
            >
              <ChevronLeft size={22} />
            </button>
            <h1 className="font-heading text-lg sm:text-xl font-bold truncate">
              {t('myAccess.title', 'Mes accès')}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="online-indicator"
              className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${
                online ? 'bg-green-500/20 text-green-200' : 'bg-orange-500/20 text-orange-200'
              }`}
            >
              {online ? <Wifi size={12} /> : <WifiOff size={12} />}
              {online ? t('myAccess.online', 'En ligne') : t('myAccess.offline', 'Hors ligne')}
            </span>
            <button
              type="button"
              onClick={refresh}
              disabled={refreshing}
              data-testid="refresh-bookings-btn"
              className="text-white/70 hover:text-white p-1.5 disabled:opacity-50"
              aria-label="Refresh"
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-5">
        {/* Cache notice */}
        {fromCache && cachedAt && (
          <div
            data-testid="cache-notice"
            className="mb-4 flex items-center gap-2 text-xs text-[#C77D63] bg-[#C77D63]/10 border border-[#C77D63]/20 rounded-md px-3 py-2"
          >
            <WifiOff size={14} />
            <span>
              {t('myAccess.cacheNotice', 'Données hors ligne du')}{' '}
              {new Date(cachedAt).toLocaleString(i18n.language)}
            </span>
          </div>
        )}

        {/* Upcoming */}
        <section className="space-y-4 mb-10">
          <h2 className="font-heading text-base font-bold text-[#1A3C34]">
            {t('myAccess.upcoming', 'À venir & en cours')}
          </h2>

          {upcoming.length === 0 ? (
            <div
              data-testid="empty-state"
              className="bg-white border border-[#1A3C34]/10 rounded-lg p-6 text-center"
            >
              <Key className="w-10 h-10 mx-auto text-[#1A3C34]/30 mb-3" />
              <p className="text-sm text-[#1A3C34]/70 mb-4">
                {t('myAccess.noBookings', "Aucune réservation à venir pour l'instant.")}
              </p>
              <Button
                onClick={() => navigate('/quick-book')}
                data-testid="empty-quick-book-btn"
                className="bg-[#C77D63] hover:bg-[#B86F58] text-white"
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                {t('myAccess.bookNow', 'Réserver maintenant')}
              </Button>
            </div>
          ) : (
            upcoming.map((b) => {
              const status = statusOf(b, now);
              const start = parseLocal(b.date, b.start_time || '00:00');
              const end = parseLocal(b.date, b.end_time || '23:59');
              const isActive = status === 'active';
              const isSoon = status === 'upcoming-soon';
              return (
                <article
                  key={b.booking_id}
                  data-testid={`booking-card-${b.booking_id}`}
                  className={`relative bg-white rounded-xl shadow-sm border-2 p-5 transition-all ${
                    isActive
                      ? 'border-green-500 shadow-lg'
                      : isSoon
                      ? 'border-[#C77D63]/50'
                      : 'border-[#1A3C34]/10'
                  }`}
                >
                  {/* Badge */}
                  <div className="absolute top-3 right-3">
                    {isActive ? (
                      <Badge className="bg-green-500 hover:bg-green-500 text-white text-[10px] uppercase tracking-wide">
                        {t('myAccess.active', 'En cours')}
                      </Badge>
                    ) : isSoon ? (
                      <Badge className="bg-[#C77D63] hover:bg-[#C77D63] text-white text-[10px] uppercase tracking-wide">
                        {t('myAccess.soon', 'Bientôt')}
                      </Badge>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wide text-[#1A3C34]/50 font-semibold">
                        {formatRelative(start, now, t)}
                      </span>
                    )}
                  </div>

                  {/* Space + date */}
                  <div className="mb-3 pr-20">
                    <h3 className="font-heading text-base font-bold text-[#1A3C34] mb-1 line-clamp-1">
                      {b.space_name || b.space_id}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-[#1A3C34]/60 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={12} />
                        {formatDateLong(b.date, i18n.language)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={12} />
                        {b.start_time}–{b.end_time}
                      </span>
                    </div>
                  </div>

                  {/* Big access code */}
                  <div className="bg-[#F9F9F7] rounded-lg p-4 mb-3 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-[#1A3C34]/50 font-semibold mb-1">
                      {t('myAccess.accessCode', "Code d'accès")}
                    </p>
                    <p
                      data-testid={`access-code-${b.booking_id}`}
                      className="font-mono text-4xl sm:text-5xl font-bold text-[#1A3C34] tracking-[0.4em] pl-[0.4em]"
                    >
                      {b.access_code}
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    {isActive && (
                      <Button
                        onClick={() => openDoor(b)}
                        disabled={openingId === b.booking_id || !online}
                        data-testid={`open-door-btn-${b.booking_id}`}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white h-12 text-base"
                      >
                        {openingId === b.booking_id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <DoorOpen className="w-5 h-5 mr-2" />
                            {t('myAccess.openDoor', 'Ouvrir la porte')}
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (navigator.clipboard) {
                          navigator.clipboard.writeText(b.access_code);
                          toast.success(t('myAccess.codeCopied', 'Code copié'));
                        }
                      }}
                      data-testid={`copy-code-btn-${b.booking_id}`}
                      className={isActive ? 'h-12' : 'flex-1 h-12'}
                    >
                      {t('myAccess.copyCode', 'Copier')}
                    </Button>
                  </div>

                  {/* Helper text */}
                  {isActive && !online && (
                    <p className="text-[11px] text-[#C77D63] mt-2 text-center">
                      {t('myAccess.offlineHint', 'Hors ligne : tapez le code sur le clavier de la porte.')}
                    </p>
                  )}
                  {isActive && (
                    <p className="text-[11px] text-[#1A3C34]/50 mt-2 text-center">
                      {t('myAccess.validUntil', 'Valable jusqu\'à')} {end?.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </article>
              );
            })
          )}
        </section>

        {/* Past (last 5) */}
        {past.length > 0 && (
          <section className="space-y-2">
            <h2 className="font-heading text-sm font-bold text-[#1A3C34]/60 uppercase tracking-wider">
              {t('myAccess.past', 'Dernières réservations')}
            </h2>
            <div className="space-y-2">
              {past.map((b) => (
                <div
                  key={b.booking_id}
                  className="bg-white border border-[#1A3C34]/10 rounded-md px-3 py-2 flex items-center justify-between text-xs"
                >
                  <span className="text-[#1A3C34]/70 truncate">
                    {formatDateLong(b.date, i18n.language)} — {b.space_name}
                  </span>
                  <span className="font-mono text-[#1A3C34]/40 ml-3">{b.access_code}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
